import {notFound} from '@tanstack/react-router'
import {createServerFn} from '@tanstack/react-start'
import {z} from 'zod'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductDefaultAppearanceId,
    getProductDefaultViewId,
    getVirtualProductId,
    parseProductId,
} from '~/utils/product'
import fetchProductDetails from '~/client/spreadshop/old/fetchProductDetails'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import fetchProductType from '~/client/sanity/fetchProductType'
import {Locale} from '~/gql/graphql'

const productParams = z.object({
    productId: z.string().regex(/[a-z0-9]+-[a-z0-9A-Z]+-[0-9]+-[0-9]+/),
    locale: z.nativeEnum(Locale).optional(),
})

export const getProduct = createServerFn({
    method: 'GET',
})
    .inputValidator((data: unknown) => {
        const result = productParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }
        return result.data
    })
    .handler(async ({data}) => {
        const {productId, locale} = data

        const [sanityProductType, productDetails] = await Promise.all([
            fetchProductType(productId, locale),
            fetchProductDetails(productId, locale),
        ])

        if (!productDetails.product) {
            throw notFound()
        }

        const {ideaId, sellableId} = parseProductId(productId)

        const imageSrc = productDetails.product.image?.src
        const baseDesignImageSrc = getCompositionImageBaseUrl(imageSrc)!

        const defaultAppearanceId = getProductDefaultAppearanceId(imageSrc)
        const defaultViewId = getProductDefaultViewId(imageSrc) ?? '1'

        const product: ProductWithIds = {
            ...productDetails.product,
            designImage: {
                src: getDesignImageSrc(baseDesignImageSrc, defaultViewId, defaultAppearanceId),
                srcset: getDesignImageSrcSet(
                    baseDesignImageSrc,
                    defaultViewId,
                    defaultAppearanceId
                ),
                altText: productDetails.product?.image?.altText,
            },
            defaultAppearanceId: getProductDefaultAppearanceId(imageSrc),
            defaultViewId: getProductDefaultViewId(imageSrc),
            vpId: getVirtualProductId(imageSrc),
            ideaId,
            sellableId,
        }

        return {
            ...product,
            images: sanityProductType?.images ?? [],
        }
    })
